import React from "react";
import { useLocation } from "react-router";

import { useNavigation } from "@refinedev/core";
import { useTable } from "@refinedev/react-table";
import { ExternalLink } from "lucide-react";

import { DataTable } from "@/components/refine-ui/data-table/data-table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  REFRESH_MODE_FILTER_OPTIONS,
  REFRESH_MODE_HINT,
} from "@/lib/tina/refresh-jobs";
import { usePersistedState } from "@/pages/tina/hooks/use-persisted-state";
import {
  type RefreshReviewFilterState,
  buildRefreshReviewFilters,
} from "@/pages/tina/refresh-review/lib/refresh-review-filters";
import { rememberRefreshReviewSearch } from "@/pages/tina/refresh-review/lib/refresh-review-return";
import { useRefreshJobsColumns } from "@/pages/tina/runs/hooks/use-refresh-jobs-columns";
import type { RefreshJobRow, RefreshMode } from "@/types/tina";

const ALL_MODES = "__all_modes__";

const isRefreshMode = (value: string | null): value is RefreshMode =>
  REFRESH_MODE_FILTER_OPTIONS.some((option) => option.value === value);

/**
 * `/tina/refresh-review` — the event-refresh HITL surface.
 *
 * **Why its own page and not a filter on `/tina/hitl`.** Same shape of answer as
 * `/tina/enrichment`, for a different reason. A refresh park is not a scrape event that stalled:
 * it is a *published* Mood event whose source page has changed under it, and the things a
 * curator reads before opening one — which event, by name; what still needs deciding; which mode
 * seeded the job — are `v_refresh_jobs` columns that `GET /events` returns only as raw `metadata`
 * jsonb. So this reads `GET /refresh-jobs?status=hitl`, where they are all columns, the filters are
 * server-side and `total` is counted over the same WHERE the page is.
 *
 * **The columns are a curator's, not the run drill-down's (ruling R77).** This page shares
 * `useRefreshJobsColumns` with the runs table but asks for its `"review"` scope, which leads with
 * the published event name and what is open, drops the ops columns, and ends with the same
 * right-pinned eye button `/tina/hitl` has — pinned here in `initialState`, exactly as
 * `items/list.tsx` pins its own. See that hook for the whole argument, including where this list's
 * row height comes from; nothing about the request on this page changed with any of it.
 *
 * The rows are still ordinary `events` rows, so double-clicking one opens the ordinary item show
 * page — where the panel registry already routes `refresh_review` to `RefreshResolution`. Nothing
 * about the resolution flow is duplicated here; this page only decides which parks a curator sees
 * and in what order.
 *
 * **It also records where in itself the curator is**, so resolving an item can put them back on
 * the same page of the same list rather than at the top of a fresh one. See
 * `lib/refresh-review-return.ts` for why that needs the URL and not just the route.
 */
export const RefreshReviewList = () => {
  const { show } = useNavigation();
  // Refine's `useTable` writes page/sorters/filters into the query string (the app sets
  // `syncWithLocation: true` globally), so the location IS this table's state. Mirrored on
  // every change rather than captured on the double-click, because a curator also leaves
  // this page through the sidebar, the back button and kbar.
  const { search } = useLocation();
  React.useEffect(() => {
    rememberRefreshReviewSearch(search);
  }, [search]);
  const columns = useRefreshJobsColumns("review");
  const [persistedMode, setMode] = usePersistedState<string | null>(
    "tina-refresh-review:mode",
    null,
  );
  // Narrowed on read rather than trusted: the value comes back from
  // localStorage, where a mode this build no longer knows could be sitting, and
  // `GET /refresh-jobs` types `mode` as an enum — an unknown value is a 422,
  // not an empty page.
  const mode = isRefreshMode(persistedMode) ? persistedMode : null;

  const filterState: RefreshReviewFilterState = { mode };
  const table = useTable<RefreshJobRow>({
    columns,
    // Same pin as `/tina/hitl`'s own table: the eye button stays reachable when
    // the row scrolls sideways, which on a queue whose whole job is "open this
    // one" is the difference between an affordance and a decoration.
    initialState: { columnPinning: { right: ["actions"] } },
    refineCoreProps: {
      // `tina-runs` + an explicit `urlPath`, the same shape `RefreshJobsTable`
      // uses: the resource only picks the data provider, and `/refresh-jobs` is
      // not a routed IMS resource of its own.
      resource: "tina-runs",
      meta: { urlPath: "refresh-jobs" },
      pagination: { pageSize: 50 },
      filters: { permanent: buildRefreshReviewFilters(filterState) },
    },
    meta: {
      onDoubleClick: (
        _e: React.MouseEvent,
        row: { original: RefreshJobRow },
      ) => {
        show("tina-items", row.original.id);
      },
    },
  });

  const setCurrentPage = table.refineCore.setCurrentPage;
  const total = table.refineCore.tableQuery.data?.total;

  // Reset to page 1 whenever the mode narrows the set, so a pick never strands
  // the table on an out-of-range page of the smaller result.
  const previousMode = React.useRef(mode);
  React.useEffect(() => {
    if (previousMode.current === mode) return;
    previousMode.current = mode;
    setCurrentPage(1);
  }, [mode, setCurrentPage]);

  // The rows on screen right now (current page, post-filter), so "all" means
  // what the curator can see, not every row the query could page through.
  const visibleRows = table.reactTable.getRowModel().rows;
  const handleOpenAllInTabs = () => {
    // Same browser cap as `/tina/hitl`: until the site is allowed to open
    // pop-ups only the first tab per click gets through, so the tooltip says so.
    for (const row of visibleRows) {
      window.open(`/tina/items/show/${row.original.id}`, "_blank");
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-semibold">
                    Refresh review
                    {typeof total === "number" ? (
                      <span className="ml-2 font-mono text-base tabular-nums text-muted-foreground">
                        {total}
                      </span>
                    ) : null}
                  </h1>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={visibleRows.length === 0}
                    onClick={handleOpenAllInTabs}
                    title="Opens every row on this page in its own tab. Your browser must allow pop-ups for this site, or only the first tab opens."
                  >
                    <ExternalLink className="h-4 w-4" />
                    Open all in tabs
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  Published events whose source page now disagrees with what
                  Mood shows, or has gone away. Nothing is overwritten until one
                  of these is resolved — Tina only fills blanks on its own.
                </p>
              </div>
              <Select
                value={mode ?? ALL_MODES}
                onValueChange={(value) =>
                  setMode(value === ALL_MODES ? null : value)
                }
              >
                <SelectTrigger className="h-9 w-[220px]">
                  <SelectValue placeholder="Any mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_MODES}>Any mode</SelectItem>
                  {REFRESH_MODE_FILTER_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="text-xs text-muted-foreground">
              {mode
                ? REFRESH_MODE_HINT[mode]
                : "Double-click a row to review the proposed changes and accept or reject them."}
            </p>

            <DataTable table={table} />
          </div>
    </div>
  );
};
